/**
 * WorkerRequestDetail — full detail page for /me/requests/:id.
 *
 * Renders 6 SMB-essential sections:
 *   1. Header: title + status chip + priority + due date
 *   2. FlowViewer: visual flow with current node highlighted
 *   3. Quick info card: assignee + started + due
 *   4. Actions row: Withdraw / Nudge / Resubmit (context-aware)
 *   5. Form data: rendered with form_schema labels
 *   6. Timeline: chronological event log
 *   7. Comments: thread with delete
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Avatar, Box, Button, Card, Chip, CircularProgress, Divider,
  IconButton, Paper, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  NotificationsActive as NudgeIcon,
  Replay as ResubmitIcon,
  Cancel as WithdrawIcon,
  Block as CancelRequestIcon,
  HistoryOutlined,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, meApi } from '../../api';
import FlowViewer from '../../components/process/FlowViewer';
import ProcessTimeline from '../../components/process/ProcessTimeline';
import { submittedOutcome } from '../../components/worker/WorkerListToolbar';
import {
  getProcessDefinitionNodeLabel,
  getSystemRequestTitle,
  timelineStepLabel,
  visibleTimelineEvents,
} from '../Processes/processDisplay';
import SubmittedApplication from '../../components/process/SubmittedApplication';
import {
} from './processFormContract';
import { ConfirmDialog } from '../../components/primitives';
import { activeLocale } from '../../utils/datetime';
import {
  safeAttachmentFilename,
  type ProcessAttachmentMeta,
} from './ProcessFormResourceValue';
import { downloadBlob } from '../../utils/downloadFile';
import { translateApiError } from '../../i18n/translateApiError';

type ToastState = {
  message: string;
  severity: 'success' | 'error' | 'info';
};

interface Comment {
  id: string;
  author_id: string;
  body: string;
  created_at: string;
}

interface TimelineEvent {
  id: string;
  actor_id: string | null;
  /** Resolved server-side; null for a system transition or an actor this tenant cannot see. */
  actor_name?: string | null;
  actor_email?: string | null;
  action: string;
  /** `approved` / `rejected` / `cancelled` on the node that carried the decision. */
  outcome?: string | null;
  /** Which flow node this event belongs to. Present on `node_entered` and the task events, and the
   *  only thing that lets a timeline row be lined up against the graph above it. */
  node_id?: string | null;
  from_step?: string | null;
  to_step?: string | null;
  notes?: string | null;
  created_at: string;
}

interface FormField {
  id?: string;
  type?: string;
  label?: string;
  properties?: { label?: string; required?: boolean; currency?: string; [key: string]: unknown };
}


const statusColor = (s: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (s === 'approved' || s === 'completed') return 'success';
  if (s === 'rejected' || s === 'failed') return 'error';
  if (s === 'submitted' || s === 'running' || s === 'in_review') return 'warning';
  if (s === 'cancelled' || s === 'withdrawn') return 'info';
  return 'default';
};

function relTime(iso: string | null | undefined, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return t('worker:requestDetail.relJustNow');
  if (ms < 3_600_000) return t('worker:requestDetail.relMinAgo', { n: Math.floor(ms / 60_000) });
  if (ms < 86_400_000) return t('worker:requestDetail.relHourAgo', { n: Math.floor(ms / 3_600_000) });
  return t('worker:requestDetail.relDayAgo', { n: Math.floor(ms / 86_400_000) });
}

const WorkerRequestDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<any>(null);
  const [detailError, setDetailError] = useState(false);
  const [commentsError, setCommentsError] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const requestTokenRef = useRef(0);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [toast, setToast] = useState<ToastState | null>(null);
  const showSuccess = (message: string) => setToast({ message, severity: 'success' });
  const showError = (caught: unknown, fallbackKey = 'common:feedback.actionFailed') => {
    setToast({
      message: caught instanceof ApiError ? translateApiError(caught, t) : t(fallbackKey),
      severity: 'error',
    });
  };
  // Issue 3.9 (Path C 2026-05-22) — withdraw uses ConfirmDialog
  // primitive instead of window.confirm() (svdy-ui §13: confirms
  // for destructive actions go through the in-app dialog, not the
  // browser's native confirm popup which can't be styled and is
  // dismissed by ESC unpredictably).
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // Declared BEFORE any early return: hooks must run in the same order every render, and
  // `formSchema` is derived after one — so this reads `request` directly instead.
  //
  // The seeded built-ins' node labels have translations; without this the worker read the
  // seed's English (`Manager approval`) under statuses that were localized. Keyed off the
  // schema's `system_key` provenance marker, so it applies to exactly the definitions that
  // HAVE translations and needs no extra fetch.
  const resolveNodeLabel = useCallback(
    (node: { id?: string; data?: { label?: string } }) => {
      const schema = request?.form_schema as { system_key?: string | null; origin_system_key?: string | null } | null;
      // The schema itself is the provenance: a template key translates; a copy's origin translates only
      // the steps still worded as the built-in (processDisplay `translatable`).
      return schema?.system_key || schema?.origin_system_key
        ? getProcessDefinitionNodeLabel(t, { name: request?.title ?? '', form_schema: schema }, node)
        : undefined;
    },
    [request, t],
  );

  /**
   * A timeline event's STEP, by name.
   *
   * Owner, 2026-09-09: the timeline has *"没有顺序上的对应关系"* with the flow diagram. The cause is
   * visible in the screenshot: `Step entered` appears three times with identical timestamps and never
   * says WHICH step, while `Approval task created — Manager approval` names its own. So a reader cannot
   * line the timeline up against the graph above it, even though every `node_entered` event carries a
   * `node_id` and the snapshot carries that node's label.
   *
   * Resolved through the SAME localizer the flow viewer uses, so one node is not called two things on
   * one page (svdy-ui §20).
   */
  // Shared with the admin instance page (`processDisplay.timelineStepLabel`), because the two surfaces had
  // two derivations and one of them did not resolve the node at all.
  const eventStepName = useCallback(
    (nodeId: string | null | undefined): string | undefined =>
      timelineStepLabel(request?.flow_snapshot, nodeId, resolveNodeLabel),
    [request, resolveNodeLabel],
  );

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    const token = ++requestTokenRef.current;
    setLoading(true);
    setRequest(null);
    setComments([]);
    setDetailError(false);
    setCommentsError(false);
    setCommentsLoading(false);
    try {
      const data = await meApi.requests.get(id);
      if (token !== requestTokenRef.current) return;
      setRequest(data);
    } catch {
      if (token === requestTokenRef.current) setDetailError(true);
      return;
    } finally {
      if (token === requestTokenRef.current) setLoading(false);
    }

    setCommentsLoading(true);
    try {
      const c = await meApi.requests.comments.list(id) as Comment[];
      if (token === requestTokenRef.current) {
        setComments(Array.isArray(c) ? c : []);
      }
    } catch {
      if (token === requestTokenRef.current) setCommentsError(true);
    } finally {
      if (token === requestTokenRef.current) setCommentsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleWithdraw = async () => {
    if (!id) return;
    try {
      await meApi.requests.withdraw(id);
      showSuccess(t('worker:requestDetail.toastWithdrawn'));
      setWithdrawOpen(false);
      fetchDetail();
    } catch (e: any) {
      showError(e);
      setWithdrawOpen(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    try {
      await meApi.requests.cancel(id);
      showSuccess(t('worker:requestDetail.toastCancelled'));
      setCancelOpen(false);
      fetchDetail();
    } catch (e: any) {
      showError(e);
      setCancelOpen(false);
    }
  };

  const handleNudge = async () => {
    if (!id) return;
    try {
      const res = await meApi.requests.nudge(id) as any;
      if (res?.success) {
        showSuccess(t('worker:requestDetail.toastReminderSent'));
      } else if (res?.next_allowed_at) {
        const next = new Date(res.next_allowed_at).toLocaleString(activeLocale());
        setToast({
          message: t('worker:requestDetail.toastReminderRateLimit', { next }),
          severity: 'info',
        });
      }
    } catch (e) { showError(e); }
  };

  const handleResubmit = async () => {
    if (!id || !request) return;
    const hasAttachments = Number(request.attachments_count ?? 0) > 0
      || (Array.isArray(request.attachments) && request.attachments.length > 0);
    if (hasAttachments) {
      const definitionId = request.definition_id;
      if (!definitionId) {
        showError(null);
        return;
      }
      navigate(
        `/me/requests/new?definitionId=${encodeURIComponent(definitionId)}`
        + `&resubmitFrom=${encodeURIComponent(id)}`,
      );
      return;
    }
    try {
      // Empty body is intentional: Backend owns the canonical source snapshot.
      // Sending request.data here would replay bound resource IDs verbatim.
      const newReq = await meApi.requests.resubmit(id, {}) as any;
      showSuccess(t('worker:requestDetail.toastResubmitted'));
      if (newReq?.id) {
        setTimeout(() => navigate(`/me/requests/${newReq.id}`), 600);
      }
    } catch (e) { showError(e); }
  };

  const handleAddComment = async () => {
    if (!id || !newComment.trim()) return;
    const targetId = id;
    const token = requestTokenRef.current;
    try {
      await meApi.requests.comments.create(targetId, newComment.trim());
      if (token !== requestTokenRef.current) return;
      setNewComment('');
      setCommentsLoading(true);
      const c = await meApi.requests.comments.list(targetId) as Comment[];
      if (token !== requestTokenRef.current) return;
      setComments(Array.isArray(c) ? c : []);
    } catch (caught) {
      if (token === requestTokenRef.current) showError(caught);
    } finally {
      if (token === requestTokenRef.current) setCommentsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!id) return;
    const targetId = id;
    const token = requestTokenRef.current;
    try {
      await meApi.requests.comments.delete(targetId, commentId);
      if (token === requestTokenRef.current) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      }
    } catch (caught) {
      if (token === requestTokenRef.current) showError(caught);
    }
  };

  const handleDownloadAttachment = async (attachment: ProcessAttachmentMeta) => {
    try {
      const blob = await meApi.processAttachments.download(attachment.id);
      downloadBlob(blob, safeAttachmentFilename(attachment.file_name));
    } catch (caught) {
      showError(caught, 'worker:processResources.downloadFailed');
    }
  };

  if (loading && !request) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;
  if (!request) return (
    <Alert
      severity="error"
      action={<Button color="inherit" size="small" onClick={fetchDetail}>{t('common:actions.retry')}</Button>}
    >
      {detailError ? t('common:feedback.loadFailed') : t('worker:requestDetail.notFound')}
    </Alert>
  );

  // Action availability
  const canWithdraw = request.can_withdraw
    ?? ['submitted', 'in_review', 'draft', 'running'].includes(request.status);
  const canCancel = request.can_cancel ?? false;
  const canNudge = ['submitted', 'in_review', 'running'].includes(request.status)
    && (request.current_assignee?.worker_id || request.current_approver_id);
  const canResubmit = ['rejected', 'cancelled', 'withdrawn'].includes(request.status);

  // Worker name resolution (svdy-rules §10.5 single source of truth).
  // Backend get_request_detail batches names for everyone the request
  // touches: requester, current_approver, current_assignee, CC list,
  // comment authors. UI reads here; if the ID isn't in the map, we
  // fall back to a 12-char slice so the display never crashes on a
  // stale ref (e.g. worker terminated mid-flight).
  const directory: Record<string, string> = request.worker_directory ?? {};
  const displayName = (workerId?: string | null): string => {
    if (!workerId) return '—';
    return directory[workerId] || workerId.slice(0, 12);
  };
  const initials = (workerId?: string | null): string => {
    const name = workerId ? directory[workerId] : null;
    if (!name) return (workerId ?? '?').slice(0, 2).toUpperCase();
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
  };
  const ccIds: string[] = Array.isArray(request.cc_worker_ids) ? request.cc_worker_ids : [];

  // Form schema for label resolution
  const formSchema: { system_key?: string | null; fields?: FormField[] } | null = request.form_schema ?? null;
  // `fieldLabel` and `fieldValue` lived here until 2026-09-09. `fieldValue` was the only one of four
  // value formatters in this codebase that appended a currency and localized a chosen option — which is
  // why THIS surface printed `100 USD` where the two admin surfaces printed `100`. It is now
  // `formatSubmittedValue` in `processFormContract`, and `SubmittedApplication` calls it for every
  // surface, so the reference cannot drift away from the pages meant to match it (svdy-ui §20).

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, width: '100%' }}>
        <IconButton size="small" onClick={() => navigate('/me/requests')}><BackIcon /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }} noWrap>
            {getSystemRequestTitle(t, request, request.title)}
          </Typography>
          {/* THE NUMBER, under the title rather than beside it: the title is what the request IS and the
              number is which one. Only rendered when present — an internal id is not an identifier a
              requester can quote, and printing one is the defect this fixes (owner, 2026-09-10, third
              report of `pin_01M202Z6`). Mobile twin: app/requests.tsx detail header (svdy-ui §13). */}
          {request.instance_number ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
              {request.instance_number}
            </Typography>
          ) : null}
        </Box>
        <Chip
          label={t(`worker:requestDetail.status_${request.status}`, { defaultValue: request.status })}
          size="small"
          color={statusColor(request.status)}
          sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1, fontWeight: 600 }}
        />
      </Stack>

      {/* Issue 3.9 (Path C 2026-05-22) — inline §13 stats line under
          the header (svdy-ui §3). Replaces the standalone Quick info
          card so the page's vertical rhythm starts with the FlowViewer
          rather than a metadata card; aggregate facts about the request
          live here as middot-separated `body2 color="text.secondary"`
          fields with bold values, exactly like /WorkerTimeEntries and
          /Webhooks. */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, ml: 5 /* match header indent past back button */ }}
      >
        {t('worker:requestDetail.submittedMeta')} <strong>{relTime(request.submitted_at ?? request.created_at, t)}</strong>
        {request.priority && (
          <>
            {' · '}{t('worker:requestDetail.priorityMeta')}{' '}
            <strong style={{ textTransform: 'capitalize' }}>{request.priority}</strong>
          </>
        )}
        {(request.current_assignee?.worker_id || request.current_assignee_id) && (
          <>
            {' · '}{t('worker:requestDetail.withMeta')}{' '}
            <strong>{request.current_assignee?.name ?? displayName(request.current_assignee_id)}</strong>
          </>
        )}
        {request.started_at && (
          <>
            {' · '}{t('worker:requestDetail.startedMeta')}{' '}<strong>{new Date(request.started_at).toLocaleDateString(activeLocale())}</strong>
          </>
        )}
        {request.due_date && (
          <>
            {' · '}{t('worker:requestDetail.dueMeta')}{' '}
            <Box
              component="strong"
              sx={{ color: 'warning.main' }}
            >
              {new Date(request.due_date).toLocaleDateString(activeLocale())}
            </Box>
          </>
        )}
      </Typography>

      {/* CC list — workers the requester wants kept in the loop.
          Hidden when empty (svdy-ui §2 — no decorative chrome on
          empty data). Tightened from a full Card to an inline row
          to keep the metadata strip compact. */}
      {ccIds.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2, ml: 5 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            CC:
          </Typography>
          {ccIds.map((wid) => (
            <Chip
              key={wid}
              label={displayName(wid)}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
            />
          ))}
        </Stack>
      )}

      {/* Flow Viewer */}
      {request.flow_snapshot && (
        <Card variant="outlined" sx={{ width: '100%', mb: 2, p: 1 }}>
          <FlowViewer
            flow={request.flow_snapshot}
            currentNodeId={request.current_node_id}
            status={request.instance_status ?? request.status}
            timeline={request.timeline}
            currentAssignee={request.current_assignee}
            approverCandidates={request.current_approver_descriptor?.candidates}
            resolveNodeLabel={resolveNodeLabel}
          />
        </Card>
      )}

      {/* Actions */}
      {(canWithdraw || canCancel || canNudge || canResubmit) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {canWithdraw && (
            <Button size="small" variant="outlined" color="warning" startIcon={<WithdrawIcon />} onClick={() => setWithdrawOpen(true)} sx={{ textTransform: 'none', borderRadius: 1 }}>
              {t('worker:requestDetail.withdrawBtn')}
            </Button>
          )}
          {canCancel && (
            <Button size="small" variant="outlined" color="error" startIcon={<CancelRequestIcon />} onClick={() => setCancelOpen(true)} sx={{ textTransform: 'none', borderRadius: 1 }}>
              {t('worker:requestDetail.cancelBtn')}
            </Button>
          )}
          {canNudge && (
            <Button size="small" variant="outlined" startIcon={<NudgeIcon />} onClick={handleNudge} sx={{ textTransform: 'none', borderRadius: 1 }}>
              {t('worker:requestDetail.sendReminder')}
            </Button>
          )}
          {canResubmit && (
            <Button size="small" variant="contained" startIcon={<ResubmitIcon />} onClick={handleResubmit} sx={{ textTransform: 'none', borderRadius: 1 }}>
              {t('worker:requestDetail.resubmit')}
            </Button>
          )}
        </Stack>
      )}

      {/* The submitted application. THIS is the surface the owner picked as the reference of the three
          (2026-09-09, screenshots 03/04), so the shared component was built from what this file did —
          its label column, its row rhythm, and its `fieldValue`, which was the only one of four
          formatters that appended a currency and localized a chosen option. The formatter now lives in
          `processFormContract` and this file calls the same component the admin pages do, so the
          reference cannot drift away from the surfaces meant to match it (svdy-ui §20). */}
      {request.data && Object.keys(request.data).length > 0 && (
        <Card variant="outlined" sx={{ width: '100%', mb: 2, p: 2 }}>
          <SubmittedApplication
            formData={request.data}
            formSchema={formSchema}
            attachments={request.attachments as ProcessAttachmentMeta[]}
            organizationCurrency={request.organization_currency}
            onDownload={handleDownloadAttachment}
            t={t}
          />
        </Card>
      )}

      {/* Timeline */}
      {request.timeline && request.timeline.length > 0 && (
        <Card variant="outlined" sx={{ width: '100%', mb: 2, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <HistoryOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('worker:requestDetail.timeline')}
            </Typography>
          </Stack>
          {/* A CONTINUOUS RAIL (owner, 2026-09-09: "timeline的蓝条没有连接起来").
              THE CAUSE was structural, not a length to tune: the rail was drawn PER ROW as a flex
              sibling of the dot, inside rows that a `Stack spacing={1.5}` separates. A connector living
              inside row N cannot cross the gap between row N and row N+1, so the rail could only ever be
              a series of stubs that reach neither the dot above nor the dot below — which is exactly what
              the screenshot shows.
              So the spacing moves INTO the row as bottom padding and the connector is absolutely
              positioned to span the row's full height. It now starts under its own dot and ends at the
              next one, with nothing between rows for it to fail to cross. */}
          {/* ONE primitive, shared with the admin page (2026-09-10). This page's dot-and-rail was the
              treatment the owner chose to standardise on, so it moved into
              `components/process/ProcessTimeline` verbatim and both surfaces render it. The anchors are
              new: they make the rail's DIRECTION explicit without inventing per-row arrows, which a
              branch-free ordered list has nothing to disambiguate. */}
          <ProcessTimeline
            rows={visibleTimelineEvents(request.timeline as TimelineEvent[], request.flow_snapshot)}
            stepName={eventStepName}
            actionLabel={(action) => t(`worker:requestDetail.action_${action}`, {
              defaultValue: t('worker:requestDetail.action_updated'),
            })}
            locale={activeLocale()}
            // TERMINAL is a FACT the anchor reports, not decoration: capping the rail under the last row
            // while the request is still moving would tell the reader it had finished.
            // Reuses the FOUR-OUTCOME bucket the filters were unified on the same day, rather than a
            // second terminal-status list: three of the four outcomes are ended, and `in_progress` is the
            // one that is not (svdy-rules §10.5b — two lists for one question is the drift).
            terminal={submittedOutcome(request.status) !== 'in_progress'}
            labels={{
              start: t('common:processTimeline.start'),
              ongoing: t('common:processTimeline.inProgress'),
              end: t('common:processTimeline.end'),
            }}
          />
        </Card>
      )}

      {/* Comments */}
      <Card variant="outlined" sx={{ width: '100%', p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
          {t('worker:requestDetail.discussion', { count: comments.length })}
        </Typography>
        {commentsError && (
          <Alert
            severity="error"
            sx={{ mb: 1.5 }}
            action={<Button color="inherit" size="small" onClick={fetchDetail}>{t('common:actions.retry')}</Button>}
          >
            {t('common:feedback.loadFailed')}
          </Alert>
        )}
        {commentsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!commentsLoading && !commentsError && comments.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {t('worker:requestDetail.noCommentsYet')}
          </Typography>
        )}
        {comments.map((c) => (
          <Paper key={c.id} elevation={0} sx={{ p: 1.5, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem', fontWeight: 700 }}>
                {initials(c.author_id)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {displayName(c.author_id)}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {relTime(c.created_at, t)}
                    </Typography>
                  </Typography>
                  <IconButton size="small" onClick={() => handleDeleteComment(c.id)} sx={{ p: 0.25 }}>
                    <DeleteIcon fontSize="small" sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                  {c.body}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" spacing={1}>
          <TextField
            size="small" fullWidth placeholder={t('worker:requestDetail.placeholderComment')}
            value={newComment} onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
          />
          <Button size="small" variant="contained" onClick={handleAddComment} disabled={!newComment.trim()} sx={{ textTransform: 'none', borderRadius: 1 }}>
            {t('worker:requestDetail.send')}
          </Button>
        </Stack>
      </Card>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.message}
        </Alert>
      </Snackbar>

      {/* Withdraw confirmation — replaces the legacy window.confirm()
          (Issue 3.9 Path C 2026-05-22). Warning tone because the
          action is reversible only via Resubmit (which creates a NEW
          request — the original stays in 'withdrawn' state). */}
      <ConfirmDialog
        open={withdrawOpen}
        tone="warning"
        title={t('worker:requestDetail.withdrawTitle')}
        description={t('worker:requestDetail.withdrawDesc')}
        subject={
          request && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {getSystemRequestTitle(t, request, request.title)}
            </Box>
          )
        }
        confirmLabel={t('worker:requestDetail.withdrawBtn')}
        confirmLoadingLabel={t('worker:requestDetail.withdrawLoading')}
        onConfirm={handleWithdraw}
        onClose={() => setWithdrawOpen(false)}
      />
      <ConfirmDialog
        open={cancelOpen}
        tone="danger"
        title={t('worker:requestDetail.cancelTitle')}
        description={t('worker:requestDetail.cancelDesc')}
        subject={
          request && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {getSystemRequestTitle(t, request, request.title)}
            </Box>
          )
        }
        confirmLabel={t('worker:requestDetail.cancelBtn')}
        confirmLoadingLabel={t('worker:requestDetail.cancelLoading')}
        onConfirm={handleCancel}
        onClose={() => setCancelOpen(false)}
      />
    </Box>
  );
};

export default WorkerRequestDetail;
